import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAdminStore } from '@/stores/adminStore';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Spinner } from '@/components/common/Spinner';
import * as adminApi from '@/api/admin';
import type { KeyEntry, GitHubAppStatus } from '@/types/admin';

export default function AdminKeysPage() {
  const { admin, clearAdmin } = useAdminStore();
  const [keys, setKeys] = useState<KeyEntry[]>([]);
  const [githubApp, setGithubApp] = useState<GitHubAppStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadKeys = useCallback(async () => {
    try {
      const response = await adminApi.listKeys();
      setKeys(response.staticKeys);
      setGithubApp(response.githubApp);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load keys');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleLogout = async () => {
    try {
      await adminApi.logout();
    } finally {
      clearAdmin();
    }
  };

  if (isLoading) {
    return <Spinner size="lg" centered />;
  }

  return (
    <div className="min-h-screen bg-md-cream py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-md-graphite">
              Key Management
            </h1>
            <p className="text-sm text-md-slate mt-1">
              Signed in as {admin?.email} ({admin?.role})
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="text-sm font-medium text-md-sky-strong hover:text-md-graphite transition-colors"
            >
              ← Admin
            </Link>
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-md-error-bg border border-md-error text-md-error p-4 rounded mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-md-success-bg border border-md-success text-md-success p-4 rounded mb-6">
            {success}
          </div>
        )}

        <Card className="mb-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-md-graphite">
              Static API Keys
            </h2>
            <AddKeyButton
              onSuccess={(msg) => {
                setSuccess(msg);
                setError(null);
                loadKeys();
              }}
              onError={(msg) => {
                setError(msg);
                setSuccess(null);
              }}
            />
          </div>
          <div className="space-y-6">
            {keys.length === 0 ? (
              <p className="text-sm text-md-slate">
                No keys configured. Click "Add Key" to add one.
              </p>
            ) : (
              keys.map((key) => (
                <KeyConfigItem
                  key={key.name}
                  entry={key}
                  onSuccess={(msg) => {
                    setSuccess(msg);
                    setError(null);
                    loadKeys();
                  }}
                  onError={(msg) => {
                    setError(msg);
                    setSuccess(null);
                  }}
                />
              ))
            )}
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-bold text-md-graphite mb-6">
            GitHub App
          </h2>
          <GitHubAppConfig
            status={githubApp}
            onSuccess={(msg) => {
              setSuccess(msg);
              setError(null);
              loadKeys();
            }}
            onError={(msg) => {
              setError(msg);
              setSuccess(null);
            }}
          />
        </Card>
      </div>
    </div>
  );
}

// --- Key Config Item ---

interface KeyConfigItemProps {
  entry: KeyEntry;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function KeyConfigItem({ entry, onSuccess, onError }: KeyConfigItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async () => {
    if (!value.trim()) return;
    setIsSaving(true);
    try {
      const response = await adminApi.setKey(entry.name, value.trim());
      onSuccess(response.message);
      setIsEditing(false);
      setValue('');
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Failed to update key');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete ${entry.name}? This will remove the key for all users.`))
      return;
    setIsDeleting(true);
    try {
      const response = await adminApi.deleteKey(entry.name);
      onSuccess(response.message);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Failed to delete key');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isEditing) {
    return (
      <div className="border-2 border-md-sky rounded p-4">
        <h3 className="font-bold text-md-graphite mb-1">{entry.name}</h3>
        {entry.description && (
          <p className="text-xs text-md-slate mb-3">{entry.description}</p>
        )}
        <Input
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Enter key value..."
        />
        <div className="flex gap-3 mt-4">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={isSaving}
            disabled={!value.trim()}
          >
            Save
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setIsEditing(false);
              setValue('');
            }}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <div>
        <h3 className="font-bold text-md-graphite">{entry.name}</h3>
        <p className="text-xs text-md-slate font-mono">{entry.envVar}</p>
        {entry.description && (
          <p className="text-xs text-md-slate">{entry.description}</p>
        )}
        <p className="text-sm mt-1">
          {entry.configured ? (
            <span className="text-md-success">
              Configured
              {entry.updatedBy && (
                <span className="text-md-slate">
                  {' '}
                  — by {entry.updatedBy}
                  {entry.updatedAt &&
                    ` on ${new Date(entry.updatedAt).toLocaleDateString()}`}
                </span>
              )}
            </span>
          ) : (
            <span className="text-md-warning">Not configured</span>
          )}
        </p>
      </div>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setIsEditing(true)}
        >
          {entry.configured ? 'Update' : 'Set'}
        </Button>
        {entry.configured && (
          <Button
            variant="danger"
            size="sm"
            onClick={handleDelete}
            loading={isDeleting}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
}

// --- Add Key Button ---

interface AddKeyButtonProps {
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function AddKeyButton({ onSuccess, onError }: AddKeyButtonProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [keyValue, setKeyValue] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const envVarPreview = keyName ? keyName.toUpperCase() : '';

  const handleSave = async () => {
    if (!keyName.trim() || !keyValue.trim()) return;
    setIsSaving(true);
    try {
      const response = await adminApi.setKey(
        keyName.trim(),
        keyValue.trim(),
        description.trim() || undefined
      );
      onSuccess(response.message);
      setIsAdding(false);
      setKeyName('');
      setKeyValue('');
      setDescription('');
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Failed to add key');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAdding) {
    return (
      <Button variant="primary" size="sm" onClick={() => setIsAdding(true)}>
        Add Key
      </Button>
    );
  }

  return (
    <div className="border-2 border-md-sky rounded p-4 space-y-3 w-full">
      <div>
        <Input
          label="Key Name (snake_case)"
          value={keyName}
          onChange={(e) => setKeyName(e.target.value.toLowerCase())}
          placeholder="e.g., testrail_api_key"
        />
        {envVarPreview && (
          <p className="text-xs text-md-slate mt-1 font-mono">
            Env var: {envVarPreview}
          </p>
        )}
      </div>
      <Input
        label="Value"
        type="password"
        value={keyValue}
        onChange={(e) => setKeyValue(e.target.value)}
        placeholder="Enter key value..."
      />
      <Input
        label="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="e.g., TestRail API key for QA automation"
      />
      <div className="flex gap-3">
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          loading={isSaving}
          disabled={!keyName.trim() || !keyValue.trim()}
        >
          Save
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setIsAdding(false);
            setKeyName('');
            setKeyValue('');
            setDescription('');
          }}
          disabled={isSaving}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

// --- GitHub App Config ---

interface GitHubAppConfigProps {
  status: GitHubAppStatus | null;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function GitHubAppConfig({ status, onSuccess, onError }: GitHubAppConfigProps) {
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [appId, setAppId] = useState('');
  const [installationId, setInstallationId] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!appId.trim() || !installationId.trim() || !privateKey.trim()) return;
    setIsSaving(true);
    try {
      const response = await adminApi.configureGitHubApp({
        appId: appId.trim(),
        installationId: installationId.trim(),
        privateKeyPem: privateKey.trim(),
      });
      onSuccess(response.message);
      setIsConfiguring(false);
      setAppId('');
      setInstallationId('');
      setPrivateKey('');
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Failed to configure GitHub App');
    } finally {
      setIsSaving(false);
    }
  };

  if (isConfiguring) {
    return (
      <div className="border-2 border-md-sky rounded p-4 space-y-4">
        <Input
          label="App ID"
          value={appId}
          onChange={(e) => setAppId(e.target.value)}
          placeholder="12345"
        />
        <Input
          label="Installation ID"
          value={installationId}
          onChange={(e) => setInstallationId(e.target.value)}
          placeholder="67890"
        />
        <div className="w-full">
          <label className="block text-sm font-medium text-md-ink mb-2">
            Private Key (PEM)
          </label>
          <textarea
            value={privateKey}
            onChange={(e) => setPrivateKey(e.target.value)}
            placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;..."
            rows={6}
            className="w-full px-4 py-2 border-2 border-md-graphite rounded bg-md-fog text-md-ink placeholder:text-md-slate focus:outline-none focus:border-md-sky-strong font-mono text-sm"
          />
        </div>
        <div className="flex gap-3">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={isSaving}
            disabled={
              !appId.trim() || !installationId.trim() || !privateKey.trim()
            }
          >
            Save & Validate
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setIsConfiguring(false);
              setAppId('');
              setInstallationId('');
              setPrivateKey('');
            }}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm">
          {status?.configured ? (
            <span className="text-md-success">
              {status.healthy ? 'Healthy' : 'Configured (unhealthy)'}
            </span>
          ) : (
            <span className="text-md-warning">Not configured</span>
          )}
        </p>
        {status?.appId && (
          <p className="text-xs text-md-slate mt-1">
            App ID: {status.appId}
            {status.installationId && ` · Installation: ${status.installationId}`}
          </p>
        )}
        {status?.message && (
          <p className="text-xs text-md-slate mt-1">{status.message}</p>
        )}
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsConfiguring(true)}
      >
        {status?.configured ? 'Reconfigure' : 'Configure'}
      </Button>
    </div>
  );
}
